import { useMemo, useState, type ReactNode } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchSelect } from '@/components/composites/search-select'
import { cn } from '@/lib/utils'
import { describeTimeZone, localTimeZone, timeZoneOptions } from '@/lib/timezone'
import { PROJECT_TYPE_LABEL } from '@/components/composites/status-badge'
import { UserAvatar } from '@/components/composites/user-avatar'
import type { Client, Project } from '@/types/entities'
import { NONE, type Person } from './pickerData'

/**
 * The pickers a work item is made of.
 *
 * A work item is created from two places now — the New work item dialog, and accepting a
 * client request — and both ask the same six questions in the same order. These are those
 * questions, extracted so the second caller inherits the first one's behaviour rather than
 * approximating it: the advisor seeding, the "one person cannot hold both approver slots"
 * rule, the 17:00 default time and the "it goes to backlog the moment this passes" warning
 * are all things that would quietly diverge in a hand-copied second form.
 *
 * Deliberately dumb. Each takes its options as a prop and fetches nothing, so the caller
 * decides when the underlying queries run (the dialogs only enable them while open).
 */

export function Required() {
  return (
    <span className="text-danger" aria-hidden>
      *
    </span>
  )
}

export function Hint({ show, children }: { show: boolean; children: ReactNode }) {
  return show ? <p className="text-tiny text-danger">{children}</p> : null
}

export function TeamSelect({
  id,
  value,
  onValueChange,
  teams,
  pending,
  invalid = false,
  emptyHint,
}: {
  id?: string
  value: string
  onValueChange: (teamId: string) => void
  teams: Array<{ id: string; label: string }>
  pending: boolean
  invalid?: boolean
  /** Why the list is empty — "this project names no teams yet" beats "no matches". */
  emptyHint?: string
}) {
  return (
    <SearchSelect
      id={id}
      value={value}
      onChange={onValueChange}
      options={teams.map((t) => ({ id: t.id, label: t.label }))}
      placeholder={pending ? 'Loading teams…' : 'Pick a team'}
      pending={pending}
      invalid={invalid}
      emptyHint={emptyHint}
    />
  )
}

/**
 * The teams delivering a work item — one or more.
 *
 * Separate from `TeamSelect` rather than a `multiple` prop on it: the single form still has
 * callers that genuinely take one team, and a component whose value is sometimes a string
 * and sometimes an array is the shape that produces a runtime surprise in whichever branch
 * is exercised second.
 */
export function TeamsSelect({
  id,
  value,
  onValueChange,
  teams,
  pending,
  invalid = false,
  emptyHint,
  showChips = true,
}: {
  id?: string
  value: string[]
  onValueChange: (teamIds: string[]) => void
  teams: Array<{ id: string; label: string }>
  pending: boolean
  invalid?: boolean
  emptyHint?: string
  showChips?: boolean
}) {
  return (
    <SearchSelect
      id={id}
      multiple
      value={value}
      onChange={onValueChange}
      options={teams.map((t) => ({ id: t.id, label: t.label }))}
      placeholder={pending ? 'Loading teams…' : 'Pick one or more teams'}
      pending={pending}
      invalid={invalid}
      emptyHint={emptyHint}
      selectAllLabel="Select all available teams"
      showChips={showChips}
    />
  )
}

export function ProjectSelect({
  id,
  value,
  onValueChange,
  projects,
  clients,
  pending,
  invalid = false,
  allowNone = false,
  onCreate,
}: {
  id?: string
  value: string
  onValueChange: (projectId: string) => void
  projects: Project[]
  clients: Client[]
  pending: boolean
  invalid?: boolean
  allowNone?: boolean
  /** Opens the New project dialog from inside the picker. Omit where that is not offered. */
  onCreate?: () => void
}) {
  const options = [
    ...(allowNone ? [{ id: NONE, label: 'No project' }] : []),
    ...projects.map((p) => ({
      id: p.id,
      label: p.name,
      hint: p.clientId
        ? (clients.find((c) => c.id === p.clientId)?.name ?? PROJECT_TYPE_LABEL[p.type])
        : PROJECT_TYPE_LABEL[p.type],
    })),
  ]
  return (
    <SearchSelect
      id={id}
      value={value}
      onChange={onValueChange}
      options={options}
      placeholder={pending ? 'Loading projects…' : 'Which engagement is this for?'}
      pending={pending}
      invalid={invalid}
      emptyHint={onCreate ? 'No projects yet.' : 'No projects yet — open one on the Projects tab first.'}
      createLabel="Open a new project"
      onCreate={onCreate}
    />
  )
}

export function PersonSelect({
  id,
  value,
  onValueChange,
  people,
  placeholder,
  /** Renders "(me)" beside this person, so the default assignee is obvious. */
  meId,
  noneLabel,
  invalid = false,
}: {
  id?: string
  value: string
  onValueChange: (personId: string) => void
  people: Person[]
  placeholder?: string
  meId?: string
  /** When given, a `NONE` option is offered with this label. */
  noneLabel?: string
  invalid?: boolean
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger id={id} aria-invalid={invalid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {noneLabel ? <SelectItem value={NONE}>{noneLabel}</SelectItem> : null}
        {people.map((p) => (
          <SelectItem key={p.id} value={p.id}>
            <span className="flex items-center gap-2">
              <UserAvatar person={p} size="xs" />
              {p.displayName}
              {p.id === meId ? <span className="text-text-muted">(me)</span> : null}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * The target end date AND time.
 *
 * Two inputs, never one: `dueAt` is a full instant and always was, and a date on its own reads
 * as "any time that day" when the real deadline may be 09:00. The pairing is what makes the
 * backlog rule legible — the note under the field says so out loud.
 */
/**
 * The handful of answers people actually give for "when is this due".
 *
 * Each one sets a date AND a time. Offering a date alone was the quiet bug in the old flow:
 * `dueAt` is an instant, so a date with no time was completed by whatever the time input
 * happened to hold, and "Friday" silently meant "Friday at 00:00" — a target that is already
 * missed when Friday starts.
 *
 * End of the working day rather than midnight, and 09:00 for the Monday one, because those are
 * what somebody means when they say tomorrow or next week.
 */
function quickTargets(now = new Date()): { label: string; date: string; time: string }[] {
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const shift = (days: number) => {
    const d = new Date(now)
    d.setDate(d.getDate() + days)
    return d
  }

  // The coming Friday. Today counts only if it IS Friday and there is still a day left of it.
  const toFriday = (5 - now.getDay() + 7) % 7
  const friday = shift(toFriday === 0 && now.getHours() >= 17 ? 7 : toFriday)
  // The next Monday, which is what "next week" means to everybody who is not a calendar.
  const monday = shift(((1 - now.getDay() + 7) % 7) || 7)

  const out = [
    // After 17:00 "today" still means today: end of the day, not a time that has already passed.
    { label: 'Today', date: iso(now), time: now.getHours() >= 17 ? '23:59' : '17:00' },
    { label: 'Tomorrow', date: iso(shift(1)), time: '17:00' },
    { label: 'Friday', date: iso(friday), time: '17:00' },
    { label: 'Next week', date: iso(monday), time: '09:00' },
  ]

  return out
}

export function DueDateTimeField({
  idPrefix,
  date,
  time,
  timeZone,
  onDate,
  onTime,
  onTimeZone,
  min,
  max,
  invalid = false,
  note,
}: {
  idPrefix: string
  date: string
  time: string
  /**
   * The zone the date and time are read in.
   *
   * A date and a time are not an instant — 14:30 is a different moment in Manila than in
   * London — and this used to be resolved with whatever zone the browser happened to be in.
   * Somebody setting a target from another office, or while travelling, meant something
   * other than what they typed.
   */
  timeZone: string
  onDate: (value: string) => void
  onTime: (value: string) => void
  onTimeZone: (value: string) => void
  min?: string
  max?: string
  invalid?: boolean
  note?: ReactNode
}) {
  const zones = useMemo(() => timeZoneOptions(), [])

  /*
   * The zone is shown as a sentence, not a control.
   *
   * Almost nobody changes it — it already defaults to the zone the browser is in — but it was
   * a full search-and-pick sitting in the flow of every single work item, which is a click and
   * a decision spent on a field whose answer was already right. It is still one tap away, and
   * it opens by itself when it is NOT the local zone, because that is the case where somebody
   * needs to see what they are agreeing to.
   */
  const presets = useMemo(
    // Never offer a date the field itself would refuse — a shortcut that produces a validation
    // error is worse than no shortcut.
    () => quickTargets().filter((p) => (!min || p.date >= min) && (!max || p.date <= max)),
    [min, max],
  )
  const local = useMemo(() => localTimeZone(), [])
  const [zoneOpen, setZoneOpen] = useState(timeZone !== local)

  return (
    <>
      {/*
        One tap for the answer people actually give.
        Every option sets a date AND a time, because a target end date without a time was being
        completed by whatever the time input happened to default to.
      */}
      <div className="mb-1.5 flex flex-wrap gap-1.5">
        {presets.map((preset) => {
          const on = date === preset.date && time === preset.time
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() => {
                onDate(preset.date)
                onTime(preset.time)
              }}
              aria-pressed={on}
              className={cn(
                'rounded-full border px-2.5 py-1 text-tiny transition-colors',
                on
                  ? 'border-brand bg-brand/10 text-brand'
                  : 'border-border text-text-secondary hover:bg-accent',
              )}
            >
              {preset.label}
            </button>
          )
        })}
      </div>

      <div className="flex gap-2">
        <Input
          id={`${idPrefix}-date`}
          type="date"
          min={min}
          max={max}
          value={date}
          onChange={(e) => onDate(e.target.value)}
          aria-invalid={invalid}
          className="num flex-1"
        />
        <Input
          id={`${idPrefix}-time`}
          type="time"
          aria-label="Target end time"
          value={time}
          onChange={(e) => onTime(e.target.value)}
          aria-invalid={invalid}
          className="num w-28 shrink-0"
        />
      </div>
      {zoneOpen ? (
        <SearchSelect
          id={`${idPrefix}-timezone`}
          options={zones}
          value={timeZone}
          onChange={onTimeZone}
          placeholder="Time zone"
          className="mt-1.5"
        />
      ) : (
        <p className="mt-1.5 text-tiny text-text-muted">
          {describeTimeZone(timeZone)}
          {' · '}
          <button
            type="button"
            onClick={() => setZoneOpen(true)}
            className="text-brand underline underline-offset-2"
          >
            change time zone
          </button>
        </p>
      )}
      {note}
    </>
  )
}

/**
 * Approver 1 and Approver 2, as two NAMED slots in order.
 *
 * `approverIds` is ordered — index 0 is Approver 1 — and the API takes them as `approver1Id` /
 * `approver2Id` rather than as an array, so the two slots are modelled as two slots here too.
 * One person cannot hold both: picking somebody already in slot 2 for slot 1 clears slot 2,
 * because the backend would otherwise dedupe them into a single approver silently.
 */
export function ApproverSlots({
  idPrefix,
  approver1Id,
  approver2Id,
  onApprover1,
  onApprover2,
  people,
  invalid1 = false,
  required1 = true,
  clash,
  hint,
}: {
  idPrefix: string
  approver1Id: string
  /** `NONE` when empty — the sentinel, not an empty string. */
  approver2Id: string
  onApprover1: (id: string) => void
  onApprover2: (id: string) => void
  people: Person[]
  invalid1?: boolean
  /**
   * Whether somebody has to be named. False when filing your own work.
   *
   * Sign-off exists so that work is not marked done by the person who did it, which has
   * nothing to say about a task you raised for yourself. The field stops being required and
   * the hint says why, rather than the form quietly accepting a blank it used to refuse.
   */
  required1?: boolean
  /** The assignee-cannot-approve warning, when it applies. */
  clash?: string | null
  hint?: ReactNode
}) {
  const hasSecondChoice = people.some((p) => p.id !== approver1Id)
  return (
    <section className="grid gap-3 rounded-lg bg-surface-sunken p-4">
      <div>
        <Label className="microlabel">Approval</Label>
        <p className="mt-1 text-tiny text-text-muted">
          {hint ??
            'Approver 1 signs this off before it can be completed. Add Approver 2 when the scope warrants a second pair of eyes. The team’s advisors are suggested.'}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor={`${idPrefix}-approver-1`}>
            Approver 1{' '}
            {required1 ? (
              <Required />
            ) : (
              <span className="font-normal text-text-muted">— optional</span>
            )}
          </Label>
          <Select
            value={approver1Id}
            onValueChange={(v) => {
              onApprover1(v)
              if (approver2Id === v) onApprover2(NONE)
            }}
          >
            <SelectTrigger
              id={`${idPrefix}-approver-1`}
              aria-invalid={invalid1}
              className="bg-card"
            >
              <SelectValue placeholder="Who signs this off?" />
            </SelectTrigger>
            <SelectContent>
              {people.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  <span className="flex items-center gap-2">
                    <UserAvatar person={p} size="xs" />
                    {p.displayName}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Hint show={invalid1}>Every work item needs an approver.</Hint>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor={`${idPrefix}-approver-2`}>Approver 2</Label>
          <Select value={approver2Id} onValueChange={onApprover2} disabled={!hasSecondChoice}>
            <SelectTrigger id={`${idPrefix}-approver-2`} className="bg-card">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>None — one approver is enough</SelectItem>
              {people
                .filter((p) => p.id !== approver1Id)
                .map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">
                      <UserAvatar person={p} size="xs" />
                      {p.displayName}
                    </span>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {!hasSecondChoice ? (
            <p className="text-tiny text-text-muted">
              Nobody else sits above the assignee, so there is no one to add as a second approver.
            </p>
          ) : null}
        </div>
      </div>
      {clash ? (
        <p className="rounded-md border border-warning bg-warning-soft px-3 py-2 text-tiny text-warning">
          {clash}
        </p>
      ) : null}
    </section>
  )
}
